import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import config from '../config/products.config.json';
import {
  HTTPError,
  IOlavEntry,
  IOrderDetailsInfo,
  IProduktVerfuegbarkeit,
  IRelocationDataDTO,
  newRelocationDetailsInfo,
  parseILCustomerAddress,
  Product,
  RelocationConfirmationStatusResponse,
} from '@mwe/models';
import {
  getAccountOwner,
  getInvoiceAddress,
  getInvoiceEmail,
  isProductCategory,
  isProductCategoryFernwaerme,
  isProductCategoryStromOrGas,
  parseInvoiceAccount,
  parseIPartnerFromClearingAccount,
  parseIRechnungsempfaengerFromClearingAccount,
  parseIVertragskontoFromClearingAccount,
  setBezahlartZahlschein,
  specialHackToCheckIfGasIsAvailable,
} from '@mwe/utils';
import { ProductCategoryEnum, ServiceStateEnum } from '@mwe/constants';
import { RelocationStateService } from './relocation-state.service';
import { OrderService } from '../order/order.service';
import { EnvironmentService } from '../environment/environment.service';
import { ClearingAccountLogic } from '../invoices/clearing-account/clearing-account.logic';
import { lastValueFrom } from 'rxjs';
import { EnvironmentCustomerType } from '@mwe/environment';
import { RelocationLogic } from './relocation.logic';
import { CsvDataService } from '../address/csv-data.service';
import { LoggingService } from '../logging/logging.service';

@Injectable({
  providedIn: 'root',
})
export class RelocationService {
  private readonly http = inject(HttpClient);
  private readonly relocationStateService = inject(RelocationStateService);
  private readonly clearingAccountLogic = inject(ClearingAccountLogic);
  private readonly relocationLogic = inject(RelocationLogic);
  private readonly orderService = inject(OrderService);
  private readonly environmentService = inject(EnvironmentService);
  private readonly csvDataService = inject(CsvDataService);
  private readonly loggingService = inject(LoggingService);

  public resourceUrl: string;
  private readonly config: any;
  private readonly inkaTitleMap = new Map<string, string>();

  constructor() {
    this.resourceUrl = this.environmentService.getApiUrl() + 'api/relocation/';
    this.config = config;
    this.inkaTitleMap.set('0001', 'Frau');
    this.inkaTitleMap.set('0002', 'Herr');
    this.inkaTitleMap.set('0003', 'Firma');
    this.inkaTitleMap.set('0004', '');
    this.inkaTitleMap.set('0005', 'Frau/Herr');
    this.inkaTitleMap.set('0006', 'Rechtsanwälte');
  }

  getAvailability(selectedOlav: IOlavEntry): Promise<IProduktVerfuegbarkeit[]> {
    return this.orderService.getAvailability(selectedOlav);
  }

  async prepareAvailabilities() {
    const selectedOlav = this.relocationStateService.relocationToOlav;
    try {
      const response = await this.getAvailability(selectedOlav);
      if (response) {
        this.fixFWAvailabilties(response);
        this.relocationStateService.relocationToAvailabilities = response;

        await specialHackToCheckIfGasIsAvailable(selectedOlav, this.relocationStateService.relocationToAvailabilities, () =>
          this.csvDataService.getCoverageArea('assets/data/gas_versorgungsgebiet.csv'),
        );

        this.relocationStateService.relocationToAvailabilitiesState = ServiceStateEnum.SUCCESS;
      } else {
        this.relocationStateService.relocationToAvailabilitiesState = ServiceStateEnum.ERROR;
      }
    } catch (error) {
      this.relocationStateService.relocationToAvailabilitiesState = ServiceStateEnum.ERROR;
      this.loggingService.logError(error, `can't load/render availabilities`);
    }
  }

  private fixFWAvailabilties(response: IProduktVerfuegbarkeit[]) {
    const fwAvailability = response.find(product => product.sparte === 'Fernwaerme');
    if (fwAvailability?.details?.[0]?.lieferkomponente === 'HEIZUNG + WARMWASSER') {
      const clonedDetail = structuredClone(fwAvailability.details[0]);
      fwAvailability.details[0].lieferkomponente = 'HEIZUNG';
      clonedDetail.lieferkomponente = 'WARMWASSER';
      fwAvailability.details.push(clonedDetail);
    }
  }

  setDeliveryOfContractData(sendContractDetailsToNewAddress: boolean): void {
    const currentRelocationDetails = this.relocationStateService.relocationDetails;
    if (currentRelocationDetails) {
      currentRelocationDetails.forEach(productDetails => {
        if (isProductCategory(productDetails.category, ProductCategoryEnum.FERNWAERME) && productDetails.status === 'Anmeldung') {
          productDetails.zustelladresse = sendContractDetailsToNewAddress ? 'to' : 'from';
        }
      });
      this.relocationStateService.relocationDetails = currentRelocationDetails;
    }
  }

  isDeliveryOfContractDataSetNewAddress(): boolean {
    const fwDewtails = this.relocationStateService.relocationDetails.find(
      productDetails => isProductCategory(productDetails.category, ProductCategoryEnum.FERNWAERME) && productDetails.status === 'Anmeldung',
    );
    return fwDewtails ? fwDewtails.zustelladresse === 'to' : false;
  }

  hasFernwaermeInNewAddressProducts(): boolean {
    return this.getNewAddressProductCategories().some(elem => isProductCategory(ProductCategoryEnum[elem], ProductCategoryEnum.FERNWAERME));
  }

  hasFernwaermeInOldAddressProducts(): boolean {
    return [...this.getNewAddressProductCategories(), ...this.getOldAddressProductCategories()].some(elem =>
      isProductCategory(ProductCategoryEnum[elem], ProductCategoryEnum.FERNWAERME),
    );
  }

  getNewAddressProductCategories(): ProductCategoryEnum[] {
    return this.relocationStateService.relocationDetails
      .filter(elem => elem.status === 'Anmeldung')
      .map(elem => ProductCategoryEnum[elem.category.toUpperCase()]);
  }

  getOldAddressProductCategories(): ProductCategoryEnum[] {
    return this.relocationStateService.relocationDetails
      .filter(elem => elem.status === 'Abmeldung')
      .map(elem => ProductCategoryEnum[elem.category.toUpperCase()]);
  }

  hasValidRelocationProduct(): boolean {
    if (this.relocationStateService.addressDetailsLinkType) {
      return false;
    }

    return !!this.relocationStateService?.relocationFromProducts?.find(
      p => isProductCategoryStromOrGas(p.category) || isProductCategoryFernwaerme(p.category),
    );
  }

  async loadDetailsData(
    products: Product[],
    orderStatus: 'Abmeldung' | 'Anmeldung',
    zustelladresse: 'from' | 'to' | '',
  ): Promise<IOrderDetailsInfo[]> {
    const newDetails: IOrderDetailsInfo[] = [];
    if (products) {
      for (const element of products) {
        const product = element;
        const customerType = this.environmentService.getPortalCustomerType();
        const orderDetail = this.relocationStateService.relocationDetails;
        if (!orderDetail) {
          const tariffObj = this.updateTariffObj(orderStatus, product, customerType);

          const orderDetailData = this.getOrderDetailData(orderStatus, zustelladresse, product, tariffObj);

          if (this.isAccountNumberEmpty(product)) {
            newDetails.push(orderDetailData);
            continue;
          }
          const alreadyLoadedDetails = this.getAlreadyLoadedDetails(newDetails, product);

          try {
            orderDetailData.verrechnungskonto = await this.getVerrechnungskonto(alreadyLoadedDetails, product);
          } catch (e) {
            throw new Error('general_error');
          }
          const accountCustomer = getAccountOwner(orderDetailData.verrechnungskonto.geschaeftspartnerBeziehungen);
          orderDetailData.physischePerson = accountCustomer.physischePerson;
          orderDetailData.invoiceAddress = parseILCustomerAddress(
            getInvoiceAddress(orderDetailData.verrechnungskonto.geschaeftspartnerBeziehungen),
          );
          const invoiceEmail = getInvoiceEmail(orderDetailData.verrechnungskonto);
          orderDetailData.eRechnungInfo = {
            isERechnungsEmpfaenger: !!invoiceEmail,
            rechnungEmail: invoiceEmail,
          };
          parseInvoiceAccount(orderDetailData);

          const rechnungsempfaenger = parseIRechnungsempfaengerFromClearingAccount(orderDetailData.verrechnungskonto);
          orderDetailData.produktDetails = {
            partner: parseIPartnerFromClearingAccount(orderDetailData.verrechnungskonto),
            vertragskonto: parseIVertragskontoFromClearingAccount(orderDetailData.verrechnungskonto),
            anschlussobjekt: null,
            rechnungsempfaenger,
            kontostand: null,
            ablesung: null,
            verbraeuche: null,
            teilbetragsplaene: null,
            rechnungen: null,
          };
          newDetails.push(orderDetailData);
        } else {
          const copy = JSON.parse(JSON.stringify(orderDetail));
          copy.status = orderStatus;
          copy.zustelladresse = zustelladresse;
          newDetails.push(copy);
        }
      }
      this.updateIfStatusIsNewOrder(orderStatus, newDetails);
    }
    return newDetails;
  }

  overrideBezahlartForFernwaermeRegistration(relocationDetails: IOrderDetailsInfo[]): void {
    relocationDetails
      .filter(relocationDetail => isProductCategory(relocationDetail.category, ProductCategoryEnum.FERNWAERME))
      .forEach(fernwaermeDetail => {
        setBezahlartZahlschein(fernwaermeDetail);
      });
  }

  showUseFernwaermeDetails(): boolean {
    return (
      this.relocationStateService.relocationFromProducts.length === 1 &&
      isProductCategory(this.relocationStateService.relocationFromProducts[0].category, ProductCategoryEnum.FERNWAERME)
    );
  }

  async confirmRelocation(): Promise<RelocationConfirmationStatusResponse> {
    try {
      return lastValueFrom(
        this.http.post<RelocationConfirmationStatusResponse>(this.resourceUrl + 'confirm', this.relocationLogic.getRelocationDTOV2()),
      ).then(res => {
        this.relocationStateService.submitId = res.submitId;
        return res;
      });
    } catch (err) {
      throw new HTTPError(err.status, err.error);
    }
  }

  async confirmStatus(submitId: number): Promise<RelocationConfirmationStatusResponse> {
    try {
      return lastValueFrom(this.http.get<RelocationConfirmationStatusResponse>(this.resourceUrl + 'confirmStatus?submitId=' + submitId));
    } catch (err) {
      throw new HTTPError(err.status, err.error);
    }
  }

  determineSummaryDetailsAddressGroupId(): string {
    return this.relocationStateService.relocationFromAddress?.addressGroup
      ? this.relocationStateService.relocationFromAddress.addressGroup.id
      : this.relocationStateService.lastOrderId;
  }

  getParsedRelocationSummaryDetails(): Promise<IRelocationDataDTO> {
    const id = this.determineSummaryDetailsAddressGroupId();
    return this.getParsedRelocationSummaryDetailsFor(id);
  }

  getRelocationSummaryDetailsFor(
    addressGroupId: string,
  ): Promise<{ id: number; processStatus: string; changeData: string; version: string; errorCode: number }> {
    return lastValueFrom(
      this.http.get<{ id: number; processStatus: string; changeData: string; version: string; errorCode: number }>(
        this.resourceUrl + 'relocationSummaryDetails?addressGroupId=' + addressGroupId,
      ),
    );
  }

  getParsedRelocationSummaryDetailsFor(addressGroupId: string): Promise<IRelocationDataDTO> {
    return this.getRelocationSummaryDetailsFor(addressGroupId).then(res => {
      return this.parseRelocationDataDTO(res);
    });
  }

  getConfigPossibleCategories(): any {
    return this.config.possibleCategories;
  }

  getConfigAvailabilityForCategory(category: string): any {
    return this.config.availability[category];
  }

  getConfigTariffs(): any {
    return this.config.tariffs;
  }

  getConfigTariffsForCategory(category: string): any {
    return this.config.tariffs[category];
  }

  private updateIfStatusIsNewOrder(orderStatus: 'Abmeldung' | 'Anmeldung', newDetails: IOrderDetailsInfo[]) {
    if (orderStatus === 'Anmeldung') {
      this.overrideBezahlartForFernwaermeRegistration(newDetails);
    }
  }

  private isAccountNumberEmpty(product: Product) {
    return !product.accountNumber || product.accountNumber === '';
  }

  private updateTariffObj(orderStatus: 'Abmeldung' | 'Anmeldung', product: Product, customerType: EnvironmentCustomerType) {
    let tariffObj: { name: string; shortcut: string; key: string };
    switch (orderStatus) {
      case 'Abmeldung':
        tariffObj = { name: product.tariffClasses[0], shortcut: '', key: '' };
        break;
      case 'Anmeldung':
        tariffObj = this.config.tariffs[product.category.toLowerCase()][customerType];
        break;
    }
    return tariffObj;
  }

  private async getVerrechnungskonto(alreadyLoadedDetails: IOrderDetailsInfo, product: Product) {
    return alreadyLoadedDetails?.verrechnungskonto
      ? alreadyLoadedDetails.verrechnungskonto
      : (await this.clearingAccountLogic.getAccountInfo(product.accountNumber, product.businessPartnerNumber, product.systemId))
          .verrechnungskonto;
  }

  private getOrderDetailData(
    orderStatus: 'Abmeldung' | 'Anmeldung',
    zustelladresse: 'from' | 'to' | '',
    product: Product,
    tariffObj: {
      name: string;
      shortcut: string;
      key: string;
    },
  ) {
    let fwProducts = [];
    if (isProductCategory(product.category, ProductCategoryEnum.FERNWAERME)) {
      fwProducts = orderStatus === 'Abmeldung' ? product.associationDetails.map(ad => ad.tarifName) : product.tariffClasses;
    }
    return newRelocationDetailsInfo(
      orderStatus,
      zustelladresse,
      product.category,
      product.businessPartnerNumber, // use business partner number in case of FW products it is different to the customer number
      product.accountNumber,
      tariffObj,
      fwProducts,
    );
  }

  private getAlreadyLoadedDetails(newDetails: IOrderDetailsInfo[], product: Product) {
    return newDetails.find(
      item =>
        item.customerNumber === product.customerNumber &&
        item.accountNumber === product.accountNumber &&
        item.category === product.category,
    );
  }

  readonly parseRelocationDataDTO = (relocationDetails: any): IRelocationDataDTO => {
    if (relocationDetails?.version === 'V2') {
      return this.relocationLogic.parseVersionTwo(JSON.parse(relocationDetails?.changeData), relocationDetails);
    } else {
      return this.relocationLogic.parseVersionOne(JSON.parse(relocationDetails?.changeData), relocationDetails);
    }
  };
}
